import { createReducer, on } from '@ngrx/store';
import * as ruleResultActions from '../actions/rule-result.actions';
import { TrendPoint } from '../../models/trend-chart.model';

export interface TrendEntry {
  rawData: TrendPoint[];
  loading: boolean;
  error?: string | null;
  fetchedAt?: number;
}

export interface RuleResultState {
  entities: Record<string, TrendEntry>;
  entitiesHealthIndicator: Record<string, TrendEntry>;
  entitiesAlarmDetails: Record<string, TrendEntry>;
}

export const initialState: RuleResultState = {
  entities: {},
  entitiesHealthIndicator: {},
  entitiesAlarmDetails: {},
};

function key(uid: string, range: string) {
  return `${uid}::${range}`;
}

export const ruleResultReducer = createReducer(
  initialState,
  on(
    ruleResultActions.loadTrend,
    (
      state: RuleResultState,
      { uid, range }: { uid: string; range: string },
    ) => {
      const k = key(uid, range);
      const prev = state.entities[k] || {
        data: null,
        options: null,
        error: null,
        loading: false,
      };
      return {
        ...state,
        entities: {
          ...state.entities,
          [k]: { ...prev, loading: true, error: null },
        },
      };
    },
  ),
  on(
    ruleResultActions.loadTrendSuccess,
    (
      state: RuleResultState,
      {
        uid,
        range,
        rawData,
        fetchedAt,
      }: {
        uid: string;
        range: string;
        rawData: TrendPoint[];
        fetchedAt: number;
      },
    ) => {
      const k = key(uid, range);
      return {
        ...state,
        entities: {
          ...state.entities,
          [k]: { rawData, loading: false, error: null, fetchedAt },
        },
      };
    },
  ),
  on(
    ruleResultActions.loadTrendFailure,
    (
      state: RuleResultState,
      { uid, range, error }: { uid: string; range: string; error: string },
    ) => {
      const k = key(uid, range);
      const prev = state.entities[k] || {
        data: null,
        options: null,
        loading: false,
      };
      return {
        ...state,
        entities: {
          ...state.entities,
          [k]: { ...prev, loading: false, error },
        },
      };
    },
  ),
  on(
    ruleResultActions.loadHealthIndicatorTrend,
    (
      state: RuleResultState,
      { uid, range }: { uid: string; range: string },
    ) => {
      const k = key(uid, range);
      const prev = state.entitiesHealthIndicator[k] || {
        rawData: [],
        loading: false,
        error: null,
      };
      return {
        ...state,
        entitiesHealthIndicator: {
          ...state.entitiesHealthIndicator,
          [k]: { ...prev, loading: true, error: null },
        },
      };
    },
  ),
  on(
    ruleResultActions.loadHealthIndicatorTrendSuccess,
    (
      state: RuleResultState,
      {
        uid,
        range,
        rawData,
        fetchedAt,
      }: {
        uid: string;
        range: string;
        rawData: TrendPoint[];
        fetchedAt: number;
      },
    ) => {
      const k = key(uid, range);
      return {
        ...state,
        entitiesHealthIndicator: {
          ...state.entitiesHealthIndicator,
          [k]: { rawData, loading: false, error: null, fetchedAt },
        },
      };
    },
  ),
  on(
    ruleResultActions.loadHealthIndicatorTrendFailure,
    (
      state: RuleResultState,
      { uid, range, error }: { uid: string; range: string; error: string },
    ) => {
      const k = key(uid, range);
      const prev = state.entitiesHealthIndicator[k] || {
        rawData: [],
        loading: false,
      };
      return {
        ...state,
        entitiesHealthIndicator: {
          ...state.entitiesHealthIndicator,
          [k]: { ...prev, loading: false, error },
        },
      };
    },
  ),
  on(
    ruleResultActions.loadAlarmDetailsTrend,
    (
      state: RuleResultState,
      { uid, range }: { uid: string; range: string },
    ) => {
      const k = key(uid, range);
      const prev = state.entitiesAlarmDetails[k] || {
        rawData: [],
        loading: false,
        error: null,
      };
      return {
        ...state,
        entitiesAlarmDetails: {
          ...state.entitiesAlarmDetails,
          [k]: { ...prev, loading: true, error: null },
        },
      };
    },
  ),
  on(
    ruleResultActions.loadAlarmDetailsTrendSuccess,
    (
      state: RuleResultState,
      {
        uid,
        range,
        rawData,
        fetchedAt,
      }: {
        uid: string;
        range: string;
        rawData: TrendPoint[];
        fetchedAt: number;
      },
    ) => {
      const k = key(uid, range);
      return {
        ...state,
        entitiesAlarmDetails: {
          ...state.entitiesAlarmDetails,
          [k]: { rawData, loading: false, error: null, fetchedAt },
        },
      };
    },
  ),
  on(
    ruleResultActions.loadAlarmDetailsTrendFailure,
    (
      state: RuleResultState,
      { uid, range, error }: { uid: string; range: string; error: string },
    ) => {
      const k = key(uid, range);
      const prev = state.entitiesAlarmDetails[k] || {
        rawData: [],
        loading: false,
      };
      return {
        ...state,
        entitiesAlarmDetails: {
          ...state.entitiesAlarmDetails,
          [k]: { ...prev, loading: false, error },
        },
      };
    },
  ),
);
